import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { useSimpleToast, getErrorMessage } from '../components/Toast';
import { Users, Calendar, TrendingUp, Loader2, CheckCircle, FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function LeaveBalanceAllocation({ user, onLogout }) {
  const { showToast, ToastComponent } = useSimpleToast();
  const [year, setYear] = useState(new Date().getFullYear());
  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterDepartment, setFilterDepartment] = useState('');
  const [dashboardStats, setDashboardStats] = useState(null);

  useEffect(() => {
    fetchBalances();
    fetchDashboardStats();
  }, [year, filterDepartment]);

  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardStats(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
    }
  };

  const fetchBalances = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      let url = `${API}/leave-balance/all?year=${year}`;
      if (filterDepartment) {
        url += `&department=${filterDepartment}`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBalances(response.data.balances || []);
    } catch (error) {
      console.error('Error fetching balances:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAllocate = async () => {
    if (!window.confirm(`Allocate annual leave for year ${year} to all active employees?\n\nThis will:\n- Create leave balance records for employees who don't have them\n- Skip employees who already have balances for this year\n- Carry forward unused leaves (if applicable)`)) {
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/leave-balance/allocate`,
        { year },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      showToast(`Allocated: ${response.data.allocated}, Skipped: ${response.data.skipped}, Total: ${response.data.total_employees}`, 'success');
      fetchBalances();
    } catch (error) {
      console.error('Allocation error:', error);
      showToast(getErrorMessage(error), 'error');
    } finally {
      setLoading(false);
    }
  };

  // Group balances by employee
  const employeeBalances = {};
  balances.forEach(balance => {
    if (!employeeBalances[balance.employee_id]) {
      employeeBalances[balance.employee_id] = {
        employee_id: balance.employee_id,
        employee_name: balance.employee_name || 'Unknown',
        employee_code: balance.employee_code || '',
        departments: balance.departments || [],
        leaves: []
      };
    }
    employeeBalances[balance.employee_id].leaves.push(balance);
  });

  const employees = Object.values(employeeBalances);

  return (
    <Layout user={user} onLogout={onLogout}>
      <ToastComponent />
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold" style={{color: '#2b3380'}}>Leave Balance Allocation</h1>
          <p className="text-gray-600 mt-2">View and allocate annual leave balances</p>
        </div>

        {/* Stats Cards - Clean white design */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-md p-5 hover:shadow-lg transition-shadow">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg" style={{backgroundColor: '#e8eaf6'}}>
                <Users className="w-6 h-6" style={{color: '#2b3380'}} />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{color: '#2b3380'}}>{employees.length}</p>
                <p className="text-sm text-gray-500">Employees</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-5 hover:shadow-lg transition-shadow">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg" style={{backgroundColor: '#e8f5e9'}}>
                <FileText className="w-6 h-6" style={{color: '#4caf50'}} />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{color: '#4caf50'}}>{balances.length}</p>
                <p className="text-sm text-gray-500">Total Balances</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-5 hover:shadow-lg transition-shadow">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg" style={{backgroundColor: '#fff3e0'}}>
                <Calendar className="w-6 h-6" style={{color: '#ff9800'}} />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{color: '#ff9800'}}>{year}</p>
                <p className="text-sm text-gray-500">Year Selected</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-5 hover:shadow-lg transition-shadow">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg" style={{backgroundColor: '#e0f2f1'}}>
                <CheckCircle className="w-6 h-6" style={{color: '#00897b'}} />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{color: '#00897b'}}>{employees.length > 0 ? '✓' : '—'}</p>
                <p className="text-sm text-gray-500">Allocation Status</p>
              </div>
            </div>
          </div>
        </div>

        {/* Actions Bar */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                <select
                  value={year}
                  onChange={(e) => setYear(parseInt(e.target.value))}
                  className="px-4 py-2 border rounded-lg"
                >
                  {[2024, 2025, 2026, 2027].map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            </div>

            <button
              onClick={handleAllocate}
              disabled={loading}
              className="px-6 py-3 text-white rounded-lg font-medium flex items-center gap-2"
              style={{backgroundColor: loading ? '#cccccc' : '#2b3380'}}
            >
              {loading ? (
                <><Loader2 className="w-5 h-5 animate-spin" />Processing...</>
              ) : (
                <><Calendar className="w-5 h-5" />Allocate Annual Leave</>
              )}
            </button>
          </div>
        </div>

        {/* Balances Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <table className="w-full">
            <thead style={{backgroundColor: '#f5f7fa'}}>
              <tr>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Employee</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Leave Type</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Allocated</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Used</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Pending</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Available</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Carried Forward</th>
              </tr>
            </thead>
            <tbody>
              {employees.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                    <Users className="w-16 h-16 mx-auto mb-4" style={{color: '#cccccc'}} />
                    <p className="text-lg font-medium mb-2">No leave balances for {year}</p>
                    <p className="text-sm">Click "Allocate Annual Leave" to create balances for all employees</p>
                  </td>
                </tr>
              ) : (
                employees.map((emp) => (
                  emp.leaves.map((leave, idx) => (
                    <tr key={`${emp.employee_id}-${leave.leave_type_id}`} className="border-t hover:bg-gray-50">
                      {idx === 0 && (
                        <td className="px-6 py-4" rowSpan={emp.leaves.length}>
                          <div>
                            <div className="text-sm font-medium">{emp.employee_name}</div>
                            <div className="text-xs text-gray-500">{emp.employee_code}</div>
                          </div>
                        </td>
                      )}
                      <td className="px-6 py-4 text-sm">{leave.leave_type_name}</td>
                      <td className="px-6 py-4 text-sm">{leave.allocated_days}</td>
                      <td className="px-6 py-4 text-sm">{leave.used_days}</td>
                      <td className="px-6 py-4 text-sm">{leave.pending_days}</td>
                      <td className="px-6 py-4 text-sm font-medium">{leave.available_days}</td>
                      <td className="px-6 py-4 text-sm text-green-600">{leave.carried_forward_days}</td>
                    </tr>
                  ))
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}

export default LeaveBalanceAllocation;